import { useState, useEffect, lazy, Suspense, startTransition, useRef } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { Toaster } from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { UserProfileDrawer } from './components/UserProfileDrawer';
import { TutorialModal, shouldShowTutorialThisSession } from './components/TutorialModal';
import { Navigation } from './components/Navigation';
import { supabase, isOtpEnabled } from './lib/supabase';
import { AuthProvider } from './contexts/AuthContext';
import { OtpProvider } from './contexts/OtpContext';
import { OtpValidation } from './components/OtpValidation';
import { ErrorBoundary } from './components/ErrorBoundary';
import { CreditsProvider } from './contexts/CreditsContext';
import { LowCreditsProvider } from './contexts/LowCreditsContext';
import { fetchSession } from './store/slices/authSlice';
import { RootState } from './store/store';
import { AppDispatch } from './store/store';
import type { AuthState } from './store/slices/authSlice';

// Lazy load components with error handling
const lazyLoad = (importFunc: () => Promise<any>) => {
  return lazy(() => {
    return new Promise((resolve) => {
      startTransition(() => {
        importFunc().then(resolve);
      });
    });
  });
};

// Lazy load components
const Home = lazyLoad(() => import('./pages/Home'));
const Login = lazyLoad(() => import('./pages/Login'));
const SignUp = lazyLoad(() => import('./pages/SignUp'));
const EmailVerification = lazyLoad(() => import('./pages/EmailVerification'));
const EmailValidationRequestPage = lazyLoad(() => import('./pages/EmailValidationRequestPage'));
const ForgotPassword = lazyLoad(() => import('./pages/ForgotPassword'));
const ResetPassword = lazyLoad(() => import('./pages/ResetPassword'));
const AdminUsers = lazyLoad(() => import('./pages/AdminUsers'));
const AdminCreditConsumption = lazyLoad(() => import('./pages/AdminCreditConsumption'));
const EditProfile = lazyLoad(() => import('./pages/EditProfile'));
const SystemParameters = lazyLoad(() => import('./pages/SystemParameters'));
const UserProfile = lazyLoad(() => import('./components/UserProfile'));
const PotentialClients = lazyLoad(() => import('./pages/PotentialClients'));

// Loading component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-900">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
  </div>
);

// Error component for Suspense fallback
const ErrorFallback = () => (
  <div className="min-h-screen bg-gray-900 flex items-center justify-center">
    <div className="text-white text-xl">Error al cargar el componente</div>
  </div>
);

const isClientRole = (role: string | undefined) =>
  role === 'user' || role === 'cliente';

function AppContent() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [tutorialWasAutoShown, setTutorialWasAutoShown] = useState(false);
  const hasAutoOpenedTutorial = useRef(false);
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const auth = useSelector((state: RootState) => state.auth) as AuthState;
  const { user, session, profile, loading } = auth;
  const hasInitialized = useRef(false);

  // Auto-abrir tutorial una vez por sesión para usuarios con rol cliente
  useEffect(() => {
    if (
      !hasAutoOpenedTutorial.current &&
      profile &&
      isClientRole(profile.role) &&
      shouldShowTutorialThisSession()
    ) {
      hasAutoOpenedTutorial.current = true;
      setTutorialWasAutoShown(true);
      setTutorialOpen(true);
    }
  }, [profile]);

  const openTutorial = () => {
    setTutorialWasAutoShown(false);
    setTutorialOpen(true);
  };

  // Cerrar el drawer automáticamente al cambiar de usuario o cuando no hay usuario
  useEffect(() => {
    if (!user) {
      console.log('🚫 Cerrando drawer - No hay usuario');
      setIsDrawerOpen(false);
    }
  }, [user]);

  useEffect(() => {
    let isMounted = true;
    console.log('🔄 AppContent useEffect ejecutándose, dispatch:', dispatch);

    // Evitar inicialización duplicada en React StrictMode
    if (hasInitialized.current) {
      console.log('⏭️ Ya inicializado, saltando...');
      return;
    }

    const initializeAuth = async () => {
      console.log('🚀 Iniciando autenticación...');
      hasInitialized.current = true;
      try {
        const result = await dispatch(fetchSession()).unwrap();
        console.log('✅ Resultado de fetchSession:', result);
      } catch (error) {
        console.error('❌ Error initializing auth:', error);
        if (!isMounted) return;
        dispatch({ type: 'auth/clearAuth' });
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase().auth.onAuthStateChange(async (event, session) => {
      console.log('🔄 Cambio en estado de autenticación:', event, session);
      if (!isMounted) return;

      if (event === 'SIGNED_IN') {
        console.log('✅ Usuario inició sesión');
        dispatch({ type: 'auth/setSession', payload: session });
      } else if (event === 'SIGNED_OUT') {
        console.log('🚪 Usuario cerró sesión - limpiando estado');
        dispatch({ type: 'auth/clearAuth' });
      }
    });

    return () => {
      console.log('🧹 Limpiando useEffect de AppContent');
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [dispatch]);

  console.log('Estado actual de auth:', { user, session, profile, loading });

  if (loading) {
    console.log('Mostrando spinner de carga...');
    return <LoadingSpinner />;
  }

  const isOtpValidationRoute = location.pathname.startsWith('/validate-otp/');
  const otpUserId = isOtpValidationRoute ? location.pathname.split('/').pop() : null;

  // Si OTP está deshabilitado, mostrar directamente el contenido autenticado
  if (!isOtpEnabled() && user) {
    console.log('OTP deshabilitado y usuario autenticado, mostrando contenido...');
    return (
      <div className="min-h-screen bg-gray-900 text-white">
        <Toaster
          position="top-left"
          // Los avisos son solo informativos: no deben bloquear los clics sobre lo que tapan
          toastOptions={{ style: { pointerEvents: 'none', maxWidth: 'min(26rem, calc(100vw - 2rem))' } }}
        />
        <Navigation
          onOpenDrawer={() => {
            console.log('📱 Abriendo drawer (OTP deshabilitado)');
            setIsDrawerOpen(true);
          }}
          onOpenTutorial={profile && isClientRole(profile.role) ? openTutorial : undefined}
        />
        <Suspense fallback={<LoadingSpinner />}>
          <ErrorBoundary fallback={<ErrorFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/email-verification" element={<EmailVerification />} />
              <Route path="/email-validation-request" element={<EmailValidationRequestPage />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/credit-consumption" element={<AdminCreditConsumption />} />
              <Route path="/edit-profile" element={<EditProfile />} />
              <Route path="/profile" element={<EditProfile />} />
              <Route path="/system-parameters" element={<SystemParameters />} />
              <Route path="/admin/parameters" element={<SystemParameters />} />
              <Route path="/potential-clients" element={<PotentialClients />} />
            </Routes>
          </ErrorBoundary>
        </Suspense>
        <UserProfileDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          onOpenTutorial={profile && isClientRole(profile.role) ? openTutorial : undefined}
        />
        <TutorialModal
          isOpen={tutorialOpen}
          onClose={() => setTutorialOpen(false)}
          wasAutoShown={tutorialWasAutoShown}
        />
      </div>
    );
  }

  // Si estamos en la ruta de validación OTP o requiere OTP, mostrar el formulario
  if ((profile && user?.id && isOtpEnabled() && profile.otp_validated === false) || (isOtpValidationRoute && otpUserId && isOtpEnabled())) {
    console.log('Mostrando validación OTP...');
    return <OtpValidation userId={otpUserId || user?.id} />;
  }

  // Si no está autenticado, mostrar rutas públicas
  if (!user) {
    console.log('Usuario no autenticado, mostrando rutas públicas...');
    return (
      <>
        <Toaster
          position="top-left"
          // Los avisos son solo informativos: no deben bloquear los clics sobre lo que tapan
          toastOptions={{ style: { pointerEvents: 'none', maxWidth: 'min(26rem, calc(100vw - 2rem))' } }}
        />
        <Suspense fallback={<LoadingSpinner />}>
          <ErrorBoundary fallback={<ErrorFallback />}>
            <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/signup/success" element={<EmailValidationRequestPage />} />
            <Route path="/verify-email/:userId" element={<EmailVerification />} />
            <Route path="/request-email-validation" element={<EmailValidationRequestPage />} />
            {isOtpEnabled() && <Route path="/validate-otp/:userId" element={<OtpValidation />} />}
            <Route path="*" element={<Login />} />
          </Routes>
        </ErrorBoundary>
      </Suspense>
      </>
    );
  }

  // Si está autenticado, mostrar rutas protegidas según el rol
  console.log('Usuario autenticado, mostrando rutas protegidas...');
  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Toaster
          position="top-left"
          // Los avisos son solo informativos: no deben bloquear los clics sobre lo que tapan
          toastOptions={{ style: { pointerEvents: 'none', maxWidth: 'min(26rem, calc(100vw - 2rem))' } }}
        />
      <Navigation
        onOpenDrawer={() => {
          console.log('📱 Abriendo drawer (rutas protegidas)');
          setIsDrawerOpen(true);
        }}
        onOpenTutorial={profile && isClientRole(profile.role) ? openTutorial : undefined}
      />
      <Suspense fallback={<LoadingSpinner />}>
        <ErrorBoundary fallback={<ErrorFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/email-verification" element={<EmailVerification />} />
            <Route path="/email-validation-request" element={<EmailValidationRequestPage />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/credit-consumption" element={<AdminCreditConsumption />} />
            <Route path="/edit-profile" element={<EditProfile />} />
            <Route path="/profile" element={<EditProfile />} />
            <Route path="/system-parameters" element={<SystemParameters />} />
            <Route path="/admin/parameters" element={<SystemParameters />} />
            <Route path="/potential-clients" element={<PotentialClients />} />
          </Routes>
        </ErrorBoundary>
      </Suspense>
      <UserProfileDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onOpenTutorial={profile && isClientRole(profile.role) ? openTutorial : undefined}
      />
      <TutorialModal
        isOpen={tutorialOpen}
        onClose={() => setTutorialOpen(false)}
        wasAutoShown={tutorialWasAutoShown}
      />
    </div>
  );
}

function AppWrapper() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <AuthProvider>
          <OtpProvider>
            <CreditsProvider>
              <LowCreditsProvider>
                <AppContent />
              </LowCreditsProvider>
            </CreditsProvider>
          </OtpProvider>
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default AppWrapper;